import { lazy, Suspense, useState } from "react";
import { Link, useParams } from "wouter";
import { useContainer, useRestartContainer, useStartContainer, useStopContainer } from "../../api/queries";
import { isNotFound } from "../../api/errors";
import { Button } from "../../components/Button";
import { ErrorState, LoadingState } from "../../components/AsyncStates";
import { ContainerStatePill } from "../../components/StatePill";
import { TabPanel, Tabs } from "../../components/Tabs";
import { useFleet } from "../../fleet/FleetContext";
import { useToast } from "../../toast/ToastContext";
import { LiveLogPanel } from "./LiveLogPanel";
import "./container-detail.css";

// xterm.js is a sizable dependency (~150kB gzipped) only the terminal tab
// needs — code-split so it's fetched on first open rather than bundled into
// every page load.
const TerminalPanel = lazy(() => import("./TerminalPanel").then((m) => ({ default: m.TerminalPanel })));

const DETAIL_TABS = [
  { id: "logs", label: "Logs" },
  { id: "terminal", label: "Terminal" },
];

export function ContainerDetailPage() {
  const { nodeId, containerId } = useParams<{ nodeId: string; containerId: string }>();
  const { currentId } = useFleet();
  const { pushToast } = useToast();
  const [activeTab, setActiveTab] = useState(DETAIL_TABS[0].id);

  const { data: container, isPending, isError, error, refetch } = useContainer(currentId, nodeId, containerId);
  const startMutation = useStartContainer(currentId);
  const stopMutation = useStopContainer(currentId);
  const restartMutation = useRestartContainer(currentId);

  const pendingAction =
    startMutation.isPending || stopMutation.isPending || restartMutation.isPending;

  function handleAction(kind: "start" | "stop" | "restart") {
    if (!containerId || !nodeId) return;
    const mutation = kind === "start" ? startMutation : kind === "stop" ? stopMutation : restartMutation;
    const verb = kind === "start" ? "started" : kind === "stop" ? "stopped" : "restarted";
    mutation.mutate(
      { nodeId, containerId },
      {
        onSuccess: () => pushToast({ title: `Container ${verb}.`, tone: "success" }),
        onError: (err) =>
          pushToast({
            title: `Couldn't ${kind} the container`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          }),
      },
    );
  }

  if (isPending) {
    return <LoadingState label="Loading container…" />;
  }

  if (isError) {
    if (isNotFound(error)) {
      return (
        <ErrorState
          title="Container not found"
          description="It may have been removed, or the link is out of date. Head back to the containers list."
        />
      );
    }
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load this container."}
        onRetry={refetch}
      />
    );
  }

  if (!container) return null;

  const canStart = container.state === "exited" || container.state === "created" || container.state === "dead";
  const canStop = container.state === "running" || container.state === "paused" || container.state === "restarting";
  const canRestart = container.state === "running" || container.state === "paused";

  return (
    <div className="container-detail">
      <Link to="/containers" className="container-detail__back">
        ← Back to containers
      </Link>

      <div className="container-detail__header">
        <div>
          <div className="container-detail__title-row">
            <h1 className="container-detail__title">{container.name}</h1>
            <ContainerStatePill state={container.state} />
          </div>
          <p className="container-detail__image">{container.image}</p>
        </div>
        <div className="container-detail__actions">
          <Button variant="secondary" disabled={!canStart || pendingAction} onClick={() => handleAction("start")}>
            {startMutation.isPending ? "Starting…" : "Start"}
          </Button>
          <Button variant="secondary" disabled={!canRestart || pendingAction} onClick={() => handleAction("restart")}>
            {restartMutation.isPending ? "Restarting…" : "Restart"}
          </Button>
          <Button variant="danger" disabled={!canStop || pendingAction} onClick={() => handleAction("stop")}>
            {stopMutation.isPending ? "Stopping…" : "Stop"}
          </Button>
        </div>
      </div>

      <dl className="container-detail__meta">
        <div>
          <dt>Status</dt>
          <dd>{container.status}</dd>
        </div>
        <div>
          <dt>Node</dt>
          <dd>{container.nodeName}</dd>
        </div>
        <div>
          <dt>Stack</dt>
          <dd>{container.stackName ?? "—"}</dd>
        </div>
        <div>
          <dt>Ports</dt>
          <dd>
            {container.ports.length === 0
              ? "—"
              : container.ports
                  .map((p) => (p.hostPort ? `${p.hostPort}→${p.containerPort}/${p.protocol}` : `${p.containerPort}/${p.protocol}`))
                  .join(", ")}
          </dd>
        </div>
        <div>
          <dt>Created</dt>
          <dd>{new Date(container.createdAt).toLocaleString()}</dd>
        </div>
      </dl>

      <Tabs items={DETAIL_TABS} activeId={activeTab} onChange={setActiveTab} ariaLabel="Container detail views" />

      <TabPanel id="logs" activeId={activeTab}>
        <LiveLogPanel fleetId={currentId} nodeId={container.nodeId} containerId={container.id} />
      </TabPanel>

      <TabPanel id="terminal" activeId={activeTab}>
        <Suspense fallback={<LoadingState label="Loading terminal…" />}>
          <TerminalPanel fleetId={currentId} nodeId={container.nodeId} containerId={container.id} />
        </Suspense>
      </TabPanel>
    </div>
  );
}
